import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrapApiResponse } from '../client';
import type { User } from '../types';

export const authKeys = {
  user: ['auth', 'user'] as const,
};

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationId?: string;
}

export function useCurrentUser() {
  return useQuery({
    queryKey: authKeys.user,
    queryFn: async () => {
      const result = await apiClient.get<User>('/auth/me');
      return unwrapApiResponse<User>(result as User | { data: User });
    },
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginRequest) =>
      apiClient.post<LoginResponse>('/auth/login', data),
    onSuccess: (data) => {
      apiClient.setAccessToken(data.accessToken);
      localStorage.setItem('accessToken', data.accessToken);
      // refreshToken is now stored as httpOnly cookie by the server — not in localStorage
      queryClient.setQueryData(authKeys.user, data.user);
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterRequest) =>
      apiClient.post<{ message: string }>('/auth/register', data),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.post('/auth/logout', {}),
    onSettled: () => {
      apiClient.setAccessToken(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      // refreshToken cookie is cleared by the server on /auth/logout
      queryClient.clear();
    },
  });
}

export function useRefreshToken() {
  return useMutation({
    mutationFn: () =>
      // No body needed — the httpOnly refreshToken cookie is sent automatically via credentials:include
      apiClient.post<LoginResponse>('/auth/refresh', {}),
    onSuccess: (data) => {
      apiClient.setAccessToken(data.accessToken);
      localStorage.setItem('accessToken', data.accessToken);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) =>
      apiClient.post<{ message: string }>('/auth/forgot-password', { email }),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      apiClient.post<{ message: string }>('/auth/reset-password', data),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { firstName: string; lastName: string }) =>
      apiClient.patch<User>('/auth/profile', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.user });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      apiClient.post<{ message: string }>('/auth/change-password', data),
  });
}

// Initialize token from localStorage on app load
export function initializeAuth() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  if (token) {
    apiClient.setAccessToken(token);
  }
}
